import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoaderCircle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  ListSubscriptionPlansQuery,
  PLAN_STATUSES,
  PlanStatus,
  SubscriptionPlan,
  SubscriptionPlanFeature,
} from "@/types/subscription-plan";
import { useSubscriptionPlans } from "../hooks/useSubscriptionPlans";
import { useDeleteSubscriptionPlan } from "../hooks/useSubscriptionPlanMutations";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { CreateCustomPlanDialog } from "./CreateCustomPlanDialog";
import { CreateDefaultPlanDialog } from "./CreateDefaultPlanDialog";
import { EditDefaultPlanDialog } from "./EditDefaultPlanDialog";
import { EditCustomPlanDialog } from "./EditCustomPlanDialog";
import { getCanonicalFeatureTitle } from "@/utils/accessControl";
import { getPlanDisplayFeatures } from "@/utils/subscriptionFeatureDisplay";

const DELETABLE_PLAN_STATUS: PlanStatus = "DRAFT";

function statusBadgeVariant(status: string) {
  switch (status) {
    case "ACTIVE":
      return "status_done";
    case "DRAFT":
      return "status_new";
    case "INACTIVE":
      return "status_canceled";
    default:
      return "new_secondary";
  }
}

function statusLabel(status: string, t: (key: string) => string): string {
  const key = `subscriptions.plans.status.${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function formatJsonCell(value: unknown): string {
  if (value === null) {
    return "—";
  }
  if (typeof value === "object" && value !== null) {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return "—";
    }
    return JSON.stringify(value);
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return "—";
}

function formatMoney(price: number | string, currency: string): string {
  const raw =
    typeof price === "number" ? price : Number.parseFloat(String(price));
  const amount = Number.isFinite(raw) ? raw : 0;
  const code = currency.trim() === "" ? "USD" : currency.trim().toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

type FeaturesCellProps = {
  readonly features: SubscriptionPlanFeature[];
  readonly enableCoreFeatures?: boolean;
};

interface NormalizedPlanTableFeature {
  key: string;
  title?: string;
  type?: string;
  value?: string;
  limit?: unknown;
}

function normalizePlanFeature(
  feature: SubscriptionPlanFeature,
): NormalizedPlanTableFeature {
  return {
    key:
      feature.key?.trim() || feature.feature?.key?.trim() || feature.featureId,
    title: feature.title ?? feature.feature?.title,
    type: feature.type ?? feature.feature?.type,
    value: feature.value,
    limit: feature.feature?.limit ?? feature.limit,
  };
}

function featureTitle(f: NormalizedPlanTableFeature): string {
  const key = String(f.key ?? "").trim();
  const title = f.title?.trim();
  return getCanonicalFeatureTitle(key, title);
}

function planDisplayFeatureLine(
  feature: ReturnType<
    typeof getPlanDisplayFeatures<NormalizedPlanTableFeature>
  >[number],
  t: (key: string) => string,
): string {
  if ("titleKey" in feature) {
    return t(feature.titleKey);
  }

  return featureLine(feature);
}

function featureLine(f: NormalizedPlanTableFeature): string {
  const title = featureTitle(f);
  const valueStr =
    f.type === "NUMBER" && typeof f.value === "string"
      ? formatLimitForDisplay(f.value)
      : null;
  const limitStr = valueStr ?? formatLimitForDisplay(f.limit);
  if (limitStr === null) {
    return title;
  }
  return `${title}: ${limitStr}`;
}

function FeaturesCell({ features, enableCoreFeatures }: FeaturesCellProps) {
  const { t } = useTranslation();
  const displayFeatures = getPlanDisplayFeatures(
    features.map(normalizePlanFeature),
    enableCoreFeatures === true,
  );

  if (displayFeatures.length === 0) {
    return <span className="text-zinc-400">—</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="new_secondary" className="cursor-default">
          {displayFeatures.length}
        </Badge>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        className="border-border bg-popover text-popover-foreground max-h-72 w-56 overflow-y-auto border p-3 shadow-lg"
      >
        <ul className="m-0 list-none space-y-1 p-0 text-left text-xs">
          {displayFeatures.map((feature, index) => (
            <li key={`${feature.key}-${index}`}>
              {planDisplayFeatureLine(feature, t)}
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

function formatLimitForDisplay(raw: unknown): string | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }
  if (typeof raw === "string") {
    const s = raw.trim();
    return s.length > 0 ? s : null;
  }
  if (typeof raw === "boolean") {
    return String(raw);
  }
  if (typeof raw === "object" && raw !== null) {
    const keys = Object.keys(raw);
    if (keys.length === 0) {
      return null;
    }
    return JSON.stringify(raw);
  }
  return null;
}

type PlansTabProps = {
  enabled: boolean;
};

export const PlansTab: React.FC<PlansTabProps> = ({ enabled }) => {
  const { t } = useTranslation();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("subscriptions");
  const [statusFilter, setStatusFilter] = useState<"all" | PlanStatus>("all");
  const [isDefaultFilter, setIsDefaultFilter] = useState<
    "all" | "true" | "false"
  >("all");
  const [createDefaultOpen, setCreateDefaultOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [editingDefaultPlan, setEditingDefaultPlan] =
    useState<SubscriptionPlan | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SubscriptionPlan | null>(
    null,
  );

  const { mutateAsync: deletePlan, isPending: isDeleting } =
    useDeleteSubscriptionPlan();

  const handleDeletePlan = async () => {
    if (deleteTarget === null) {
      return;
    }

    try {
      await deletePlan(deleteTarget.id);
      toast.success(t("subscriptions.plans.delete.successToast"));
      setDeleteTarget(null);
    } catch {
      // BaseService already surfaces the backend's error message via toast
    }
  };

  const listQuery = useMemo((): ListSubscriptionPlansQuery => {
    const q: ListSubscriptionPlansQuery = {};
    if (statusFilter !== "all") {
      q.status = statusFilter;
    }
    if (isDefaultFilter === "true") {
      q.isDefault = true;
    } else if (isDefaultFilter === "false") {
      q.isDefault = false;
    }
    return q;
  }, [isDefaultFilter, statusFilter]);

  const { data, isLoading } = useSubscriptionPlans(enabled, listQuery);

  const rows = data ?? [];

  const columns: Column<SubscriptionPlan>[] = useMemo(() => {
    return [
      {
        key: "title",
        header: t("subscriptions.plans.table.title"),
        className: "align-top",
      },
      {
        key: "description",
        header: t("subscriptions.plans.table.description"),
        className: "align-top",
        render: (_: unknown, row: SubscriptionPlan) => (
          <span className="line-clamp-2 max-w-[220px] text-sm text-zinc-600">
            {formatJsonCell(row.description)}
          </span>
        ),
      },
      {
        key: "price",
        header: t("subscriptions.plans.table.price"),
        className: "align-top text-center text-sm whitespace-nowrap",
        render: (_: unknown, row: SubscriptionPlan) =>
          formatMoney(row.price, row.currency),
      },
      {
        key: "status",
        header: t("subscriptions.plans.table.status"),
        className: "align-top text-center",
        render: (value: string) => (
          <Badge variant={statusBadgeVariant(value)}>
            {statusLabel(value, t)}
          </Badge>
        ),
      },
      {
        key: "isDefault",
        header: t("subscriptions.plans.table.isDefault"),
        className: "align-top text-center text-sm",
        render: (value: boolean) => (
          <Badge variant={value ? "new_default" : "new_secondary"}>
            {value ? t("subscriptions.plans.yes") : t("subscriptions.plans.no")}
          </Badge>
        ),
      },
      {
        key: "isCustom",
        header: t("subscriptions.plans.table.isCustom"),
        className: "align-top text-center text-sm",
        render: (value: boolean) => (
          <Badge variant={value ? "new_default" : "new_secondary"}>
            {value ? t("subscriptions.plans.yes") : t("subscriptions.plans.no")}
          </Badge>
        ),
      },
      {
        key: "enableCoreFeatures",
        header: t("subscriptions.plans.table.corePlatform"),
        className: "align-top text-center",
        render: (value: boolean | undefined) => (
          <Badge variant={value ? "new_default" : "new_secondary"}>
            {value ? t("subscriptions.plans.yes") : t("subscriptions.plans.no")}
          </Badge>
        ),
      },
      {
        key: "features",
        header: t("subscriptions.plans.table.features"),
        className: "align-top text-center w-20",
        render: (_: unknown, row: SubscriptionPlan) => (
          <FeaturesCell
            features={row.features}
            enableCoreFeatures={row.enableCoreFeatures}
          />
        ),
      },
      {
        key: "stripePriceId",
        header: t("subscriptions.plans.table.stripePriceId"),
        className: "align-top text-center text-sm max-w-[120px]",
        render: (_: unknown, row: SubscriptionPlan) => (
          <span className="line-clamp-1">
            {formatJsonCell(row.stripePriceId)}
          </span>
        ),
      },
      {
        key: "tenantId",
        header: t("subscriptions.plans.table.tenantId"),
        className: "align-top text-sm max-w-[140px]",
        render: (_: unknown, row: SubscriptionPlan) => (
          <span className="line-clamp-2 font-mono text-xs">
            {formatJsonCell(row.tenantId)}
          </span>
        ),
      },
      {
        key: "actions",
        header: t("subscriptions.plans.table.actions"),
        className: "align-top text-right w-24",
        render: (_: unknown, row: SubscriptionPlan) => {
          const canDeleteRow = row.status === DELETABLE_PLAN_STATUS;
          const deleteTitle = canDeleteRow
            ? t("subscriptions.plans.delete.action")
            : t("subscriptions.plans.delete.actionDisabledNotDraft");

          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                type="button"
                disabled={!canWrite}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.isCustom) {
                    setEditingPlan(row);
                  } else {
                    setEditingDefaultPlan(row);
                  }
                }}
                aria-label={
                  row.isCustom
                    ? t("subscriptions.plans.custom.editPlan")
                    : t("subscriptions.plans.default.editPlan")
                }
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                disabled={!canWrite || !canDeleteRow}
                title={deleteTitle}
                aria-label={deleteTitle}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(row);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ];
  }, [t, canWrite]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm leading-none font-medium text-zinc-900">
              {t("subscriptions.plans.filters.status")}
            </span>
            <Select
              value={statusFilter}
              onValueChange={(value) =>
                setStatusFilter(value as "all" | PlanStatus)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("subscriptions.plans.filters.all")}
                </SelectItem>
                {PLAN_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {statusLabel(s, t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm leading-none font-medium text-zinc-900">
              {t("subscriptions.plans.filters.planType")}
            </span>
            <Select
              value={isDefaultFilter}
              onValueChange={(value) =>
                setIsDefaultFilter(value as "all" | "true" | "false")
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("subscriptions.plans.filters.all")}
                </SelectItem>
                <SelectItem value="true">
                  {t("subscriptions.plans.filters.typeDefault")}
                </SelectItem>
                <SelectItem value="false">
                  {t("subscriptions.plans.filters.typeCustom")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="new_secondary"
            disabled={!canWrite}
            onClick={() => setCreateDefaultOpen(true)}
          >
            {t("subscriptions.plans.default.createPlan")}
          </Button>
          <Button
            type="button"
            variant="new_default"
            disabled={!canWrite}
            onClick={() => setCreateOpen(true)}
          >
            {t("subscriptions.plans.custom.createPlan")}
          </Button>
        </div>
      </div>

      <DataTable<SubscriptionPlan>
        data={rows}
        columns={columns}
        loading={isLoading}
        columnHeaderClassName="text-sm font-medium text-neutral-500"
        emptyMessage={t("subscriptions.plans.table.empty")}
        withPagination={false}
        withSorting={false}
      />
      <CreateCustomPlanDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <CreateDefaultPlanDialog
        open={createDefaultOpen}
        onClose={() => setCreateDefaultOpen(false)}
      />
      <EditCustomPlanDialog
        plan={editingPlan}
        onClose={() => setEditingPlan(null)}
      />
      <EditDefaultPlanDialog
        plan={editingDefaultPlan}
        onClose={() => setEditingDefaultPlan(null)}
      />
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isDeleting) {
            setDeleteTarget(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("subscriptions.plans.delete.title")}</DialogTitle>
            <DialogDescription>
              {t("subscriptions.plans.delete.description", {
                planTitle: deleteTarget?.title ?? "—",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="new_secondary"
              disabled={isDeleting}
              onClick={() => setDeleteTarget(null)}
            >
              {t("subscriptions.plans.delete.cancel")}
            </Button>
            <Button
              type="button"
              variant="new_destructive"
              disabled={isDeleting || deleteTarget === null}
              onClick={() => void handleDeletePlan()}
            >
              {isDeleting ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : null}
              {t("subscriptions.plans.delete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
